import {
  FileText,
  Building2,
  Lock,
  CreditCard,
  BookOpen,
  Mail,
  ChevronRight,
  Clock,
  CalendarClock,
} from 'lucide-react'
import { NavLink } from 'react-router-dom'

const phase1Features = [
  {
    icon: FileText,
    title: 'A9 + A10 typeformular',
    desc: 'Standardkontrakterne genereres automatisk og udfyldes fra dine lejemålsdata. Ingen Word-filer.',
  },
  {
    icon: Lock,
    title: 'MitID-signering',
    desc: 'Lejer og udlejer signerer digitalt med MitID via Criipto. Juridisk bindende fra dag ét.',
  },
  {
    icon: Building2,
    title: 'BBR-integration',
    desc: 'Ejendomsoplysninger hentes automatisk fra Bygnings- og Boligregistret (BBR) ved oprettelse.',
  },
  {
    icon: FileText,
    title: 'Lejeroprettelse med CPR-kryptering',
    desc: 'CPR-numre krypteres med AES-256-GCM. Audit-log på enhver læsning. GDPR-korrekt fra dag ét.',
  },
  {
    icon: BookOpen,
    title: '§ 11-betingelser',
    desc: 'Tilpas standardvilkår med egne §11-klausuler. Skabelonbibliotek medfølger.',
  },
  {
    icon: Mail,
    title: 'E-mail notifikationer',
    desc: 'Lejer får besked når kontrakten er klar til signering. Automatisk kvittering ved underskrift.',
  },
]

const phase2Features = [
  {
    icon: CreditCard,
    title: 'Månedlig fakturering',
    desc: 'Fakturaer dannes idempotent første bankdag i måneden. Ingen manuelle kørsler.',
  },
  {
    icon: BookOpen,
    title: 'Manuel betalingsregistrering',
    desc: 'Registrer bankoverførsler direkte i systemet. Fuldt overblik over restancer.',
  },
  {
    icon: Building2,
    title: 'e-conomic integration',
    desc: 'Bogføring eksporteres automatisk til e-conomic. Understøtter dimensioner og projekter.',
  },
]

const plannedFeatures = [
  {
    title: 'NPI-regulering',
    desc: 'Automatisk huslejeregulering baseret på nettoprisindekset. Lovpligtig 3-måneders varsling.',
  },
  {
    title: '§ 93 påkrav',
    desc: 'Fristberegning, gebyrberegning og påkravsskrivelse genereres korrekt.',
  },
  {
    title: 'Betalingsservice (PBS)',
    desc: 'Direkte integration — automatisk indbetaling og afstemning.',
  },
  {
    title: 'Lejerportal',
    desc: 'Lejer ser næste betaling, dokumenter og beskeder — alt ét sted.',
  },
  {
    title: 'Ind- og udflytningssyn',
    desc: 'Mobil-app til synsregistrering med billeder og underskrift.',
  },
  {
    title: 'Kommunikationsmodul',
    desc: 'Send beskeder til en eller alle lejere med skabeloner og personalisering.',
  },
]

export default function FeaturesPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-navy-950 text-white py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-5">Funktioner</h1>
          <p className="text-navy-200 text-lg leading-relaxed">
            Bygget op om dansk lejelovgivning. Kontrakter og signering først — opkrævning og regulering følger.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-cream">
        <div className="max-w-6xl mx-auto space-y-20">

          {/* Phase 1 — Building now */}
          <div>
            <div className="flex items-center gap-3 mb-8 pb-3 border-b border-navy-200">
              <h2 className="text-2xl font-bold text-navy-950">Kontrakter &amp; signering</h2>
              <span className="inline-flex items-center gap-1.5 bg-navy-100 text-navy-700 text-xs font-semibold px-3 py-1 rounded-full">
                <Clock size={12} />
                Bygges nu
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {phase1Features.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white rounded-2xl p-7 border border-navy-100">
                  <div className="w-10 h-10 bg-navy-100 rounded-xl flex items-center justify-center mb-5">
                    <Icon size={20} className="text-navy-700" />
                  </div>
                  <h3 className="text-navy-950 font-semibold text-base mb-2">{title}</h3>
                  <p className="text-navy-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 2 — Coming soon */}
          <div>
            <div className="flex items-center gap-3 mb-8 pb-3 border-b border-navy-200">
              <h2 className="text-2xl font-bold text-navy-950">Opkrævning &amp; bogføring</h2>
              <span className="inline-flex items-center gap-1.5 bg-cream border border-navy-200 text-navy-600 text-xs font-semibold px-3 py-1 rounded-full">
                <CalendarClock size={12} />
                Kommer snart
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {phase2Features.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white rounded-2xl p-7 border border-navy-100 opacity-80">
                  <div className="w-10 h-10 bg-navy-50 rounded-xl flex items-center justify-center mb-5">
                    <Icon size={20} className="text-navy-400" />
                  </div>
                  <h3 className="text-navy-700 font-semibold text-base mb-2">{title}</h3>
                  <p className="text-navy-500 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 3+ — Planned */}
          <div>
            <div className="flex items-center gap-3 mb-8 pb-3 border-b border-navy-200">
              <h2 className="text-2xl font-bold text-navy-950">Planlagt</h2>
              <span className="inline-flex items-center gap-1.5 bg-cream border border-navy-200 text-navy-500 text-xs font-semibold px-3 py-1 rounded-full">
                Roadmap
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {plannedFeatures.map(({ title, desc }) => (
                <div key={title} className="bg-white rounded-xl p-5 border border-navy-100 opacity-60">
                  <h3 className="text-navy-700 font-semibold text-sm mb-1">{title}</h3>
                  <p className="text-navy-500 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-950 py-16 px-6 text-white text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Klar til at prøve?</h2>
          <p className="text-navy-300 mb-8">Start gratis. Opsæt dine første lejemål på under 10 minutter.</p>
          <NavLink
            to="/pricing"
            className="bg-white text-navy-950 font-semibold px-8 py-3 rounded-lg hover:bg-cream transition-colors inline-flex items-center gap-2"
          >
            Se priser <ChevronRight size={18} />
          </NavLink>
        </div>
      </section>
    </>
  )
}
